/**
 * The Import modal (SPEC `Content Performance` § Import modal, endpoints #90-#92).
 *
 * Four blocks in one dialog: a dropzone, the synchronous import report, the list of stored sessions
 * with Delete behind a confirm, and — stacked on top when the server refuses — the handle-clash
 * question.
 *
 * The import is **synchronous in the request** (SPEC decision 25): there is no job, no SSE and no
 * polling, so the response *is* the report and the only progress state is "uploading". What that
 * makes non-obvious is the `409`: it persisted nothing, and the approved retry is a plain re-post of
 * the same `File` with `confirm=1`. `contentImportReducer` keeps those apart — this file holds the
 * `File` itself (a reducer never holds a handle) and fires the request; every phase decision is the
 * reducer's, and every count and warning on screen is a server string (§12.5).
 *
 * Both mutations drop the whole `['content']` root: an import or a delete re-resolves the merged
 * dataset server-side, so the charts, both table pages and this session list are all stale at once.
 */

import { useReducer, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContentImportReportDTO, ContentSessionDTO } from '@lw/shared';
import {
  deleteContentSession,
  getContentSessions,
  importContentArchive,
} from '../../api/content';
import { QK } from '../../api/queryKeys';
import { describeError, isApiError } from '../../api/client';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Modal,
  Spinner,
  UploadIcon,
  cx,
} from '../../components';
import { formatInteger } from '../../lib/format';
import {
  INITIAL_CONTENT_IMPORT_STATE,
  canSubmitContentImport,
  contentHandleMismatchMessage,
  contentImportReducer,
  parseContentHandleMismatch,
  validateContentImportFile,
} from './contentState';

export type ImportDialogProps = {
  open: boolean;
  onClose: () => void;
};

/** The report's rows, in the order the pipeline produces them. Labels are the only words here. */
function reportRows(report: ContentImportReportDTO): { label: string; display: string }[] {
  return [
    { label: 'Series rows merged', display: report.seriesRowsMergedDisplay },
    { label: 'Posts added', display: report.postsAddedDisplay },
    { label: 'Posts updated', display: report.postsUpdatedDisplay },
    { label: 'Comments added', display: report.commentsAddedDisplay },
    { label: 'Comments updated', display: report.commentsUpdatedDisplay },
    { label: 'Own-post comments excluded', display: report.ownPostCommentsExcludedDisplay },
  ];
}

export function ImportDialog({ open, onClose }: ImportDialogProps) {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(contentImportReducer, INITIAL_CONTENT_IMPORT_STATE);
  // The reducer tracks the *name*; the handle itself stays here, so a re-post after the 409 sends
  // the same bytes without asking the user to pick the file again.
  const fileRef = useRef<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ContentSessionDTO | null>(null);

  const sessionsQuery = useQuery({
    queryKey: QK.contentSessions(),
    queryFn: ({ signal }) => getContentSessions(signal),
    enabled: open,
  });
  const sessions = sessionsQuery.data?.sessions ?? [];

  const upload = useMutation({
    mutationFn: (input: { file: File; confirm: boolean }) =>
      importContentArchive(input.file, { confirm: input.confirm }),
    onSuccess: (result) => {
      dispatch({ type: 'succeeded', result });
      void queryClient.invalidateQueries({ queryKey: QK.content });
    },
    onError: (error) => {
      // A 409 carrying handle-clash details is a question, not a failure: nothing was persisted.
      const mismatch = isApiError(error)
        ? parseContentHandleMismatch(error.status, error.details)
        : null;
      if (mismatch !== null) dispatch({ type: 'mismatch', details: mismatch });
      else dispatch({ type: 'failed', message: describeError(error) });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteContentSession(id),
    onSuccess: () => {
      setPendingDelete(null);
      void queryClient.invalidateQueries({ queryKey: QK.content });
    },
  });

  const pickFile = (file: File) => {
    const refusal = validateContentImportFile(file);
    if (refusal !== null) {
      fileRef.current = null;
      dispatch({ type: 'rejected', message: refusal });
      return;
    }
    fileRef.current = file;
    dispatch({ type: 'pick', fileName: file.name });
  };

  const post = (confirm: boolean) => {
    const file = fileRef.current;
    if (file === null) return;
    upload.mutate({ file, confirm });
  };

  const submit = () => {
    dispatch({ type: 'submit' });
    post(false);
  };

  const approve = () => {
    dispatch({ type: 'approve' });
    post(true);
  };

  const close = () => {
    if (state.phase === 'uploading') return;
    dispatch({ type: 'reset' });
    fileRef.current = null;
    onClose();
  };

  const uploading = state.phase === 'uploading';

  return (
    <>
      <Modal
        open={open}
        title="Import LinkedIn analytics"
        size="lg"
        closeOnBackdrop={!uploading}
        onClose={close}
        footer={
          <>
            <button type="button" className="table-action" onClick={close} disabled={uploading}>
              <span className="table-action__label">Close</span>
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={submit}
              disabled={!canSubmitContentImport(state)}
            >
              {uploading ? <Spinner size="sm" /> : <UploadIcon />}
              {uploading ? 'Importing…' : 'Import'}
            </button>
          </>
        }
      >
        <div className="cn-import">
          {/* ------------------------------------------------------------ dropzone */}
          <div
            className={cx('cn-drop', dragging && 'is-over')}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              const file = event.dataTransfer.files.item(0);
              if (file !== null) pickFile(file);
            }}
          >
            <UploadIcon size={20} />
            <p className="cn-drop__lead">
              Drop the <code>.zip</code> your extension produced, or pick it.
            </p>
            <label className="btn-ghost cn-drop__pick">
              Choose file
              <input
                type="file"
                accept=".zip,application/zip"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.item(0) ?? null;
                  // Cleared so re-picking the same file still fires a change event.
                  event.target.value = '';
                  if (file !== null) pickFile(file);
                }}
              />
            </label>
            {state.fileName === null ? null : (
              <p className="cn-drop__file">{state.fileName}</p>
            )}
          </div>

          {state.phase === 'error' && state.errorMessage !== null ? (
            <ErrorState title="That import did not go through" description={state.errorMessage} />
          ) : null}

          {/* -------------------------------------------------------------- report */}
          {state.phase === 'done' && state.result !== null ? (
            <div className="cn-report">
              <h3 className="cn-block__title">Imported</h3>
              <dl className="cn-report__grid">
                {reportRows(state.result.report).map((row) => (
                  <div className="cn-report__row" key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{row.display}</dd>
                  </div>
                ))}
              </dl>
              {state.result.report.warnings.length > 0 ? (
                <ul className="cn-warnings">
                  {state.result.report.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {/* ------------------------------------------------------------ sessions */}
          <div className="cn-block">
            <h3 className="cn-block__title">Stored imports</h3>

            {sessionsQuery.isPending ? <Spinner label="Loading imports…" /> : null}

            {sessionsQuery.isError ? (
              <ErrorState
                title="Couldn't list the stored imports"
                error={sessionsQuery.error}
                onRetry={() => void sessionsQuery.refetch()}
              />
            ) : null}

            {sessionsQuery.isSuccess && sessions.length === 0 ? (
              <EmptyState
                title="Nothing imported yet"
                description="Each ZIP you import is kept as its own session, and the screen merges them on read."
              />
            ) : null}

            {sessions.length > 0 ? (
              <ul className="cn-sessions">
                {sessions.map((session) => (
                  <li className="cn-session" key={session.id}>
                    <div className="cn-session__main">
                      <span className="cn-session__when">
                        Imported {session.importedAtDisplay}
                      </span>
                      <span className="cn-session__meta">
                        {[
                          session.vanityHandle === null ? null : `@${session.vanityHandle}`,
                          `exported ${session.exportedAtDisplay}`,
                          session.extensionVersion === null
                            ? null
                            : `extension ${session.extensionVersion}`,
                          session.schemaVersion === null
                            ? null
                            : `schema ${String(session.schemaVersion)}`,
                          `${formatInteger(session.fileCount)} files`,
                          session.rawBytesDisplay,
                        ]
                          .filter((part): part is string => part !== null)
                          .join(' · ')}
                      </span>
                      <span className="cn-session__meta">
                        {`${formatInteger(session.counts.seriesRows)} series rows · ${formatInteger(
                          session.counts.posts,
                        )} posts · ${formatInteger(session.counts.comments)} comments`}
                      </span>
                    </div>

                    {session.warnings.length > 0 ? (
                      <span
                        className="cn-session__warn"
                        title={session.warnings.join('\n')}
                        aria-label={`${formatInteger(session.warnings.length)} warnings`}
                      >
                        ⚠ {formatInteger(session.warnings.length)}
                      </span>
                    ) : null}

                    <button
                      type="button"
                      className="table-action btn-danger"
                      onClick={() => setPendingDelete(session)}
                    >
                      <span className="table-action__label">Delete</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {remove.isError ? (
              <ErrorState title="Couldn't delete that import" error={remove.error} />
            ) : null}
          </div>
        </div>
      </Modal>

      {/* The handle clash, stacked over the modal: the server answered 409 and stored nothing. */}
      <ConfirmDialog
        open={state.phase === 'confirm' && state.mismatch !== null}
        title="Different account"
        message={state.mismatch === null ? '' : contentHandleMismatchMessage(state.mismatch)}
        confirmLabel="Import anyway"
        cancelLabel="Don't import"
        onConfirm={approve}
        onCancel={() => dispatch({ type: 'decline' })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this import?"
        message="Removes this import's data from the merged dataset and its stored archive; other imports are untouched."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={() => {
          if (pendingDelete !== null) remove.mutate(pendingDelete.id);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}

export default ImportDialog;
